import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Gift, TrendingUp, ShoppingBag, Search } from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";

const Rewards = () => {
  const navigate = useNavigate();
  const { getRewardsList, getPointSummary } = useGlobal();

  const [products, setProducts] = useState([]);
  const [pointSummary, setPointSummary] = useState({});
  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [canRedeemOnly, setCanRedeemOnly] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const initialLoad = async () => {
      const [data, points] = await Promise.all([
        getRewardsList(""),
        getPointSummary(),
      ]);
      setPointSummary(points || {});
      setProducts(data || []);
    };
    initialLoad();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const searchLoad = async () => {
      const data = await getRewardsList(search);
      setProducts(data || []);
    };
    searchLoad();
  }, [search]);

  const isOutOfStock = (status) =>
    status?.label?.toLowerCase() === "out of stock";

  const balance = pointSummary?.balance || 0;

  const displayedProducts = products.filter((p) => {
    if (inStockOnly && isOutOfStock(p.status)) return false;
    if (canRedeemOnly && p.points > balance) return false;
    return true;
  });

  return (
    <div className="content-part">
      {/* Stats + Goal */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-lg-6">
          <div className="db-stats h-100">
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--gold">
                <Trophy size={14} />
              </div>
              <div>
                <div className="db-stat-val">{pointSummary?.balance || 0}</div>
                <div className="db-stat-lbl">Balance</div>
              </div>
            </div>
            <div className="db-stat-divider" />
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--blue">
                <TrendingUp size={14} />
              </div>
              <div>
                <div className="db-stat-val">
                  {pointSummary?.total_earned || 0}
                </div>
                <div className="db-stat-lbl">Earned</div>
              </div>
            </div>
            <div className="db-stat-divider" />
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--red">
                <ShoppingBag size={14} />
              </div>
              <div>
                <div className="db-stat-val">
                  {pointSummary?.total_redeemed || 0}
                </div>
                <div className="db-stat-lbl">Redeemed</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="db-goal h-100">
            <div className="db-goal-header">
              <span className="db-goal-label">
                <i className="fa-solid fa-trophy" />
                Reward Summary
              </span>
              <span className="db-goal-pct">
                {pointSummary?.total_redeemed} /{" "}
                {pointSummary?.total_earned || 0} pts
              </span>
            </div>
            {(() => {
              const earned = pointSummary?.total_earned || 0;
              const redeemed = pointSummary?.total_redeemed || 0;

              const progressWidth =
                earned > 0 ? `${(redeemed / earned) * 100}%` : "0%";

              return (
                <>
                  <div className="db-goal-track mt-3">
                    <div
                      className="db-goal-fill"
                      style={{ width: progressWidth }}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Section header */}
      <div className="rw-section-header mb-3">
        <div>
          <h5 className="rw-section-title">Exclusive Rewards</h5>
          <p className="rw-section-sub">Redeem your points for amazing items</p>
        </div>
        <div className="d-flex align-items-center gap-3 rw-controls">
          <div className="form-group position-relative mb-0">
            <Search
              className="position-absolute text-muted"
              size={16}
              style={{
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              type="text"
              className="form-control border-0 bg-light rounded-pill fs-15 input-tags"
              style={{ paddingLeft: "36px", height: "40px" }}
              placeholder="Search rewards..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="rw-filter-pills">
            <button
              className={`rw-filter-pill${inStockOnly ? " active-stock" : ""}`}
              onClick={() => setInStockOnly((v) => !v)}
            >
              In Stock
            </button>
            <button
              className={`rw-filter-pill${canRedeemOnly ? " active-redeem" : ""}`}
              onClick={() => setCanRedeemOnly((v) => !v)}
            >
              Can Redeem
            </button>
          </div>
          <span className="rw-balance-badge">
            <Trophy size={12} />
            {balance} pts available
          </span>
        </div>
      </div>

      {displayedProducts.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <Gift size={48} className="mb-3 opacity-40" />
          <p className="fw-bold mb-1">
            {canRedeemOnly
              ? "No rewards match your current balance."
              : inStockOnly
                ? "No in-stock rewards available right now."
                : search?.trim()
                  ? `No results found for "${search}"`
                  : "No rewards available right now."}
          </p>
          {search?.trim() && !inStockOnly && !canRedeemOnly && (
            <p className="mb-0 fs-14 text-muted">
              Try searching with another keyword.
            </p>
          )}
        </div>
      ) : (
        <div className="row g-3">
          {displayedProducts.map((item) => {
            const oos = isOutOfStock(item.status);
            const cannotAfford = item.points > balance;
            return (
              <div key={item.product_pid} className="col-6 col-xl-3">
                <div
                  className={`rw-card${oos ? " rw-card--oos" : ""}`}
                  role={oos ? undefined : "button"}
                  tabIndex={oos ? -1 : 0}
                  onClick={() =>
                    !oos &&
                    navigate("/reward-redeem", {
                      state: { product_pid: item.product_pid },
                    })
                  }
                  onKeyDown={(e) => {
                    if (oos) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/reward-redeem", {
                        state: { product_pid: item.product_pid },
                      });
                    }
                  }}
                  style={{ cursor: oos ? "not-allowed" : "pointer" }}
                >
                  <div className="rw-card-img">
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = images.placeHolderImg;
                      }}
                    />
                    {oos && (
                      <span
                        className="rw-stock-badge"
                        style={{
                          background: item.status.bg_color,
                          color: item.status.text_color,
                        }}
                      >
                        {item.status.label}
                      </span>
                    )}
                  </div>
                  <div className="rw-card-body">
                    <h6 className="rw-card-title">{item.name}</h6>
                    <div className="rw-card-footer">
                      <div className="rw-card-points">
                        <Gift size={14} />
                        <span>
                          {item.points} <small>pts</small>
                        </span>
                      </div>
                      <button
                        type="button"
                        className="rw-redeem-btn"
                        disabled={oos || cannotAfford}
                        tabIndex={-1}
                      >
                        {oos ? "Unavailable" : "Redeem"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Rewards;
